import { useEffect } from 'react';
import { useTour } from '@reactour/tour';
import { getActiveTourKey } from './activeTour';
import { CANAPPS_HOME_STEP } from './tourRegistry';
import {
	setHomeTourAdvanceFromNav,
	homeTourCloseProfileMenu,
} from './homeTourState';

const HOME_TOUR_KEY = 'canapps_navigation';
const PROFILE_MENU_SELECTOR = '[data-tour="nav-profile-menu"]';

// Drives the CANapps Home Tour's optional "open the CANtabs menu" interaction.
// Runs in SharedLayout, which owns the sidebar open state. Two jobs:
//  - Register the branch for the nav-menu step's Next: skip straight to the
//    profile step if the sidebar is already open, else show the open-menu step.
//  - Once the operator opens the sidebar (from the open-menu step), auto-advance
//    to the profile step. Handling both here means "already open" and "just
//    opened" converge without ever flashing the open-menu step.
export const useHomeTourControl = (sidebarOpen: boolean): void => {
	const { isOpen, currentStep, setCurrentStep } = useTour();

	// Keep the nav-step Next branch in sync with the live sidebar state.
	useEffect(() => {
		setHomeTourAdvanceFromNav(() => {
			setCurrentStep(
				sidebarOpen
					? CANAPPS_HOME_STEP.PROFILE
					: CANAPPS_HOME_STEP.OPEN_SIDEBAR,
			);
		});
		return () => setHomeTourAdvanceFromNav(null);
	}, [sidebarOpen, setCurrentStep]);

	// Advance to the profile step as soon as the sidebar is open.
	useEffect(() => {
		if (!isOpen || getActiveTourKey() !== HOME_TOUR_KEY) return;
		if (currentStep === CANAPPS_HOME_STEP.OPEN_SIDEBAR && sidebarOpen) {
			setCurrentStep(CANAPPS_HOME_STEP.PROFILE);
		}
	}, [isOpen, currentStep, sidebarOpen, setCurrentStep]);

	// On the profile step, advance to the dropdown step once the operator opens
	// the profile menu (its portal appears in the DOM). Watched, not clicked, so
	// it works however the menu opens. We advance after a short delay so the menu's
	// grow animation has settled — reactour measures the target once when the step
	// activates and a transform-scale mid-animation doesn't retrigger its
	// observers, so measuring too early leaves the spotlight on the wrong rect.
	useEffect(() => {
		if (!isOpen || getActiveTourKey() !== HOME_TOUR_KEY) return;
		if (currentStep !== CANAPPS_HOME_STEP.PROFILE) return;

		let timer: ReturnType<typeof setTimeout> | undefined;
		const advance = () => {
			timer = setTimeout(
				() => setCurrentStep(CANAPPS_HOME_STEP.PROFILE_MENU),
				300,
			);
		};
		const found = () => !!document.querySelector(PROFILE_MENU_SELECTOR);
		if (found()) {
			advance();
			return () => clearTimeout(timer);
		}
		const observer = new MutationObserver(() => {
			if (found()) {
				observer.disconnect();
				advance();
			}
		});
		observer.observe(document.body, { childList: true, subtree: true });
		return () => {
			observer.disconnect();
			clearTimeout(timer);
		};
	}, [isOpen, currentStep, setCurrentStep]);

	// Close the profile dropdown once the tour leaves the profile/menu steps, so
	// the open menu doesn't linger (dimmed) behind later steps.
	useEffect(() => {
		if (!isOpen || getActiveTourKey() !== HOME_TOUR_KEY) return;
		if (
			currentStep !== CANAPPS_HOME_STEP.PROFILE &&
			currentStep !== CANAPPS_HOME_STEP.PROFILE_MENU
		) {
			homeTourCloseProfileMenu();
		}
	}, [isOpen, currentStep]);
};
